import {defineStore} from "pinia";
import {ref, Ref, UnwrapRef} from "vue";
import {CartState} from "@/Types/index.d";
import {
    DiscountResource,
    CartLineResource,
    CartResource,
    ProductResource,
    SubscriptionPlanResource,
    VariantResource
} from "@/Types/generated.d";
import axios, {AxiosResponse} from "axios";
import {useModalStore} from "@/Stores/ModalStore";
import ModalAddedToCartConfirmation from "@/Pages/Frontend/Cart/Partials/ModalAddedToCartConfirmation.vue";
import {router} from "@inertiajs/vue3";

export const useCartStore = defineStore('cartStore', () => {
    const modalStore = useModalStore();
    const state: Ref<UnwrapRef<CartState>> = ref<CartState>({
        loading: false,
        cart: null,
        cartLinesCount: 0,
        crossSellingProducts: [],
    })

    const getCart = async(): Promise<void> => {
        try {
            const response: AxiosResponse<CartResource> = await axios.get(route('frontend.cart.show'))

            state.value.cart = response.data
            state.value.cartLinesCount = response.data.cart_lines?.length ?? 0

        } catch (error) {
            console.log(error)
        }
    }

    const crossSelling = async(): Promise<void> => {
        try {
            const response: AxiosResponse<ProductResource[]> = await axios.get(route('frontend.cart.cross-selling'))

            state.value.crossSellingProducts = response.data

        } catch (error) {
            console.log(error)
        }
    }

    const addProduct = (
        product: ProductResource,
        variant: VariantResource,
        subscriptionPlan: SubscriptionPlanResource | null = null,
        customizationText: string | null = null,
        quantity: number = 1,
        showConfirmModal: boolean = true
    ): void => {
        try {
            router.post(route('frontend.cart.store', [variant.id, subscriptionPlan?.id]), {
                quantity: quantity,
                customization_text: customizationText,
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    if (showConfirmModal) {
                        openModalAddedToCartConfirmation(product, variant, subscriptionPlan, customizationText, quantity)
                    }
                },
                onStart: () => state.value.loading = true,
                onFinish: () => state.value.loading = false,
            })
        } catch (error) {
            console.log(error)
        }
    }

    const updateCartLine = (cartLine: CartLineResource, quantity: number): void => {
        try {
            router.patch(route('frontend.cart.update', cartLine.id), {
                quantity: quantity,
            }, {
                preserveScroll: true,
                onStart: () => state.value.loading = true,
                onFinish: () => state.value.loading = false,
            })
        } catch (error) {
            console.log(error)
        }
    }

    const deleteCartLine = (cartLine: CartLineResource): void => {
        try {
            router.delete(route('frontend.cart.destroy', cartLine.id), {
                preserveScroll: true,
                onStart: () => state.value.loading = true,
                onFinish: () => state.value.loading = false,
            })
        } catch (error) {
            console.log(error)
        }
    }

    const addDiscount = (code: string): void => {
        try {
            router.post(route('frontend.cart.discount.store'), {
                discount_code: code,
            }, {
                preserveScroll: true,
                onStart: () => state.value.loading = true,
                onFinish: () => state.value.loading = false,
            })
        } catch (error) {
            console.log(error)
        }
    }

    const deleteDiscount = (discount: DiscountResource): void => {
        try {
            router.delete(route('frontend.cart.discount.destroy', discount.id), {
                preserveScroll: true,
                onStart: () => state.value.loading = true,
                onFinish: () => state.value.loading = false,
            })
        } catch (error) {
            console.log(error)
        }
    }

    const openModalAddedToCartConfirmation = (
        product: ProductResource,
        variant: VariantResource,
        subscriptionPlan: SubscriptionPlanResource | null = null,
        customizationText: string | null = null,
        quantity: number = 1,
    ) => {
        modalStore.open({
            component: ModalAddedToCartConfirmation,
            props: {
                product: product,
                variant: variant,
                subscriptionPlan: subscriptionPlan,
                customizationText: customizationText,
                quantity: quantity,
            },
        })
    };

    return {
        state,
        getCart,
        crossSelling,
        addProduct,
        updateCartLine,
        deleteCartLine,
        addDiscount,
        deleteDiscount,
        openModalAddedToCartConfirmation
    }
})
